/**
 * Vertalingen voor de vaste widget-UI (knoppen, labels, foutmeldingen). Dienstnamen
 * horen hier NIET bij — die typt de klant zelf in (zie Service.nameEn in
 * types/models.ts): de widget toont nameEn in EN-modus en valt terug op de
 * Nederlandse naam als er geen Engelse vertaling is ingevuld.
 */

export type Lang = "nl" | "en";

/** Vertaalt een willekeurige waarde (embed-attribuut, opgeslagen voorkeur, ...) naar
 * een geldige Lang — onbekende/ontbrekende waarden vallen terug op Nederlands. */
export function normalizeLang(value?: string | null): Lang {
    return value === "en" ? "en" : "nl";
}

/** Locale voor Intl-formattering (datum/tijd/bedrag) per taal. Bedrag blijft altijd EUR
 * — alleen de opmaak (volgorde, scheidingstekens) verandert mee met de taal. */
export function localeFor(lang: Lang): string {
    return lang === "en" ? "en-GB" : "nl-NL";
}

export interface WidgetStrings {
    stepLabel: (step: number, total: number) => string;
    langToggleAria: string;

    modal: {
        title: string;
        closeAria: string;
    };

    location: {
        title: string;
        copy: string;
        selected: string;
        loading: string;
    };

    service: {
        title: string;
        copy: string;
        selected: string;
        minSuffix: string;
        loading: string;
        infoPanel: string;
        /** Badge op een dienst met capaciteit > 1 — bijv. "Groepsles". */
        groupBadge: string;
        /** Categorie-tab die alle diensten toont, ongeacht categorie. */
        allCategories: string;
        /** Naam van de impliciete groep voor diensten zonder eigen categorie. */
        otherCategory: string;
        searchPlaceholder: string;
        noResults: string;
    };

    staff: {
        title: string;
        copy: string;
        badgeSelected: string;
        badgeAvailable: string;
        linkChange: string;
        linkSelect: string;
        loading: string;
        roleFallbacks: [string, string, string, string];
    };

    dateTime: {
        kicker: string;
        title: string;
        copy: string;
        minutesSuffix: string;
        prevMonthAria: string;
        nextMonthAria: string;
        weekdays: [string, string, string, string, string, string, string];
        availableTimesTitle: string;
        loadingSlots: string;
        noSlots: string;
        /** Voor groepslessen (capaciteit > 1) — hoeveel plekken er nog vrij zijn op dit
         * exacte tijdstip. */
        spotsLeft: (n: number) => string;
    };

    details: {
        title: string;
        copy: string;
        nameLabel: string;
        namePlaceholder: string;
        emailLabel: string;
        emailPlaceholder: string;
        phoneLabel: string;
        phonePlaceholder: string;
        verificationLabel: string;
        verificationEmail: string;
        verificationPhone: string;
        notesLabel: string;
        notesPlaceholder: string;
        summaryTitle: string;
        summaryLocation: string;
        summaryService: string;
        summaryStaff: string;
        summaryDate: string;
        summaryTime: string;
        whatsappOptInLabel: string;
        whatsappOptInHint: string;
        summaryPrice: string;
        summaryDiscountApplied: string;
        summaryDeposit: string;
        depositNotice: string;
    };

    success: {
        kicker: string;
        title: string;
        message: (reservationId: number) => string;
        newBooking: string;
        close: string;
    };

    nav: {
        prev: string;
        next: string;
        submitting: string;
        submit: string;
    };

    processing: {
        title: string;
        copy: string;
    };

    /** Wachtlijst als een dag vol is — zie WaitlistForm. */
    waitlist: {
        cta: string;
        intro: string;
        partOfDayLabel: string;
        partOfDay: { ANY: string; MORNING: string; AFTERNOON: string; EVENING: string };
        phoneLabel: string;
        submit: string;
        sending: string;
        done: string;
        alreadyOnList: string;
        failed: string;
    };
    errors: {
        chooseLocationFirst: string;
        chooseServiceFirst: string;
        chooseStaffFirst: string;
        chooseTimeFirst: string;
        fillEverything: string;
        needPhoneForVerification: string;
        chooseValidTime: string;
        bookingFailed: string;
        loadServicesFailed: string;
        loadStaffFailed: string;
        loadAvailabilityFailed: string;
        /** Tijdstip is tussen het ophalen van de beschikbaarheid en het boeken alsnog
         * bezet/te dichtbij geraakt (bv. de klant nam de tijd om het formulier in te
         * vullen) — generieke, vriendelijke melding i.p.v. de technische serverfout. */
        slotNoLongerAvailable: string;
        invalidEmail: string;
        /** Proefperiode/abonnement van het bedrijf verlopen — zie BillingAccessService. */
        bookingsClosed: string;
    };
}

const nl: WidgetStrings = {
    stepLabel: (step, total) => `Stap ${step} van ${total}`,
    langToggleAria: "Taal",

    modal: {
        title: "Boek een afspraak",
        closeAria: "Sluiten",
    },

    location: {
        title: "Kies een vestiging",
        copy: "Bij welke vestiging wilt u een afspraak maken?",
        selected: "Geselecteerd",
        loading: "Vestigingen laden…",
    },

    service: {
        title: "Kies een dienst",
        copy: "Selecteer de behandeling die u wilt reserveren.",
        selected: "Geselecteerd",
        minSuffix: "min",
        loading: "Diensten laden…",
        infoPanel: "U kunt in de volgende stap een specifieke medewerker kiezen indien gewenst.",
        groupBadge: "Groepsles",
        allCategories: "Alle diensten",
        otherCategory: "Overig",
        searchPlaceholder: "Zoek een dienst",
        noResults: "Geen diensten gevonden.",
    },

    staff: {
        title: "Kies een medewerker",
        copy: "Selecteer de specialist die u wilt boeken voor uw afspraak.",
        badgeSelected: "Geselecteerd",
        badgeAvailable: "Beschikbaar",
        linkChange: "WIJZIGEN",
        linkSelect: "SELECTEER",
        loading: "Medewerkers laden…",
        roleFallbacks: ["Senior Specialist", "Hoofd Stylist", "Specialist", "Junior Stylist"],
    },

    dateTime: {
        kicker: "Afspraak",
        title: "Datum & Tijd selectie",
        copy: "Kies een geschikte datum in de kalender en selecteer vervolgens een beschikbaar tijdstip.",
        minutesSuffix: "minuten",
        prevMonthAria: "Vorige maand",
        nextMonthAria: "Volgende maand",
        weekdays: ["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"],
        availableTimesTitle: "Beschikbare tijden",
        loadingSlots: "Beschikbare tijden laden…",
        noSlots: "Geen beschikbare tijden.",
        spotsLeft: (n) => (n === 1 ? "nog 1 plek" : `nog ${n} plekken`),
    },

    details: {
        title: "Uw gegevens",
        copy: "Vul uw gegevens in om de reservering af te ronden.",
        nameLabel: "Naam",
        namePlaceholder: "Bijv. Jan Jansen",
        emailLabel: "Email",
        emailPlaceholder: "naam@mail.com",
        phoneLabel: "Telefoon",
        phonePlaceholder: "+31 6 12345678",
        verificationLabel: "Verificatie methode",
        verificationEmail: "Email",
        verificationPhone: "Telefoon",
        notesLabel: "Notities",
        notesPlaceholder: "Bijv. graag kort aan de zijkanten",
        summaryTitle: "Overzicht",
        summaryLocation: "Vestiging",
        summaryService: "Dienst",
        summaryStaff: "Medewerker",
        summaryDate: "Datum",
        summaryTime: "Tijd",
        whatsappOptInLabel: "Stuur mijn herinnering via WhatsApp",
        whatsappOptInHint: "Je krijgt één bericht vóór je afspraak. Vul hiervoor je telefoonnummer in.",
        summaryPrice: "Prijs",
        summaryDiscountApplied: "Korting toegepast",
        summaryDeposit: "Aanbetaling",
        depositNotice: "Om je afspraak te bevestigen, wordt na het boeken een aanbetaling gevraagd.",
    },

    success: {
        kicker: "Afspraak",
        title: "Bijna klaar!",
        message: (id) =>
            `We hebben je een bevestigingsmail gestuurd. Klik op de link in je mail om je afspraak te bevestigen. (Reservering #${id})`,
        newBooking: "Nieuwe boeking",
        close: "Sluiten",
    },

    nav: {
        prev: "Vorige",
        next: "Volgende",
        submitting: "Boeking verwerken...",
        submit: "Boek afspraak",
    },

    processing: {
        title: "Bezig met verwerken…",
        copy: "We maken je reservering aan en sturen direct een bevestigingsmail.",
    },

    waitlist: {
        cta: "Zet me op de wachtlijst voor deze dag",
        intro: "Komt er een plek vrij op deze dag, dan krijg je meteen een mail. Wie het eerst boekt, heeft 'm.",
        partOfDayLabel: "Wanneer kun je?",
        partOfDay: { ANY: "Hele dag", MORNING: "Ochtend", AFTERNOON: "Middag", EVENING: "Avond" },
        phoneLabel: "Telefoon (optioneel)",
        submit: "Op de wachtlijst",
        sending: "Bezig…",
        done: "Je staat op de wachtlijst. We mailen je zodra er een plek vrijkomt.",
        alreadyOnList: "Je staat al op de wachtlijst voor deze dag.",
        failed: "Aanmelden is niet gelukt. Probeer het opnieuw.",
    },
    errors: {
        chooseLocationFirst: "Kies eerst een vestiging.",
        chooseServiceFirst: "Kies eerst een dienst.",
        chooseStaffFirst: "Kies eerst een medewerker.",
        chooseTimeFirst: "Kies eerst een tijdstip.",
        fillEverything: "Vul alles in (service, staff, datum, tijd, naam, email).",
        needPhoneForVerification: "Vul een telefoonnummer in als je verificatie via telefoon kiest.",
        chooseValidTime: "Kies een geldig tijd.",
        bookingFailed: "Boeking mislukt",
        loadServicesFailed: "Kon diensten niet laden",
        loadStaffFailed: "Kon medewerkers voor deze dienst niet laden",
        loadAvailabilityFailed: "Kon beschikbaarheid niet laden",
        slotNoLongerAvailable: "Dit tijdstip is helaas niet meer beschikbaar. Kies een ander tijdstip.",
        invalidEmail: "Vul een geldig e-mailadres in.",
        bookingsClosed: "Online boeken is op dit moment niet mogelijk. Neem direct contact op met het bedrijf om een afspraak te maken.",
    },
};

const en: WidgetStrings = {
    stepLabel: (step, total) => `Step ${step} of ${total}`,
    langToggleAria: "Language",

    modal: {
        title: "Book an appointment",
        closeAria: "Close",
    },

    location: {
        title: "Choose a location",
        copy: "Which location would you like to book at?",
        selected: "Selected",
        loading: "Loading locations…",
    },

    service: {
        title: "Choose a service",
        copy: "Select the treatment you'd like to book.",
        selected: "Selected",
        minSuffix: "min",
        loading: "Loading services…",
        infoPanel: "You can choose a specific staff member in the next step if you'd like.",
        groupBadge: "Group class",
        allCategories: "All services",
        otherCategory: "Other",
        searchPlaceholder: "Search a service",
        noResults: "No services found.",
    },

    staff: {
        title: "Choose a staff member",
        copy: "Select the specialist you'd like to book for your appointment.",
        badgeSelected: "Selected",
        badgeAvailable: "Available",
        linkChange: "CHANGE",
        linkSelect: "SELECT",
        loading: "Loading staff…",
        roleFallbacks: ["Senior Specialist", "Lead Stylist", "Specialist", "Junior Stylist"],
    },

    dateTime: {
        kicker: "Appointment",
        title: "Date & time selection",
        copy: "Choose a suitable date on the calendar, then select an available time.",
        minutesSuffix: "minutes",
        prevMonthAria: "Previous month",
        nextMonthAria: "Next month",
        weekdays: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
        availableTimesTitle: "Available times",
        loadingSlots: "Loading available times…",
        noSlots: "No available times.",
        spotsLeft: (n) => (n === 1 ? "1 spot left" : `${n} spots left`),
    },

    details: {
        title: "Your details",
        copy: "Fill in your details to complete the booking.",
        nameLabel: "Name",
        namePlaceholder: "e.g. John Doe",
        emailLabel: "Email",
        emailPlaceholder: "name@mail.com",
        phoneLabel: "Phone",
        phonePlaceholder: "+31 6 12345678",
        verificationLabel: "Verification method",
        verificationEmail: "Email",
        verificationPhone: "Phone",
        notesLabel: "Notes",
        notesPlaceholder: "e.g. please keep it short on the sides",
        summaryTitle: "Summary",
        summaryLocation: "Location",
        summaryService: "Service",
        summaryStaff: "Staff member",
        summaryDate: "Date",
        summaryTime: "Time",
        whatsappOptInLabel: "Send my reminder via WhatsApp",
        whatsappOptInHint: "You'll get one message before your appointment. Enter your phone number for this.",
        summaryPrice: "Price",
        summaryDiscountApplied: "Discount applied",
        summaryDeposit: "Deposit",
        depositNotice: "To confirm your appointment, a deposit is requested after booking.",
    },

    success: {
        kicker: "Appointment",
        title: "Almost done!",
        message: (id) =>
            `We've sent you a confirmation email. Click the link in the email to confirm your appointment. (Booking #${id})`,
        newBooking: "New booking",
        close: "Close",
    },

    nav: {
        prev: "Previous",
        next: "Next",
        submitting: "Processing booking...",
        submit: "Book appointment",
    },

    processing: {
        title: "Processing…",
        copy: "We're creating your booking and will send a confirmation email right away.",
    },

    waitlist: {
        cta: "Join the waitlist for this day",
        intro: "If a spot opens up on this day, you'll get an email right away. First to book gets it.",
        partOfDayLabel: "When are you available?",
        partOfDay: { ANY: "All day", MORNING: "Morning", AFTERNOON: "Afternoon", EVENING: "Evening" },
        phoneLabel: "Phone (optional)",
        submit: "Join waitlist",
        sending: "Sending…",
        done: "You're on the waitlist. We'll email you as soon as a spot opens up.",
        alreadyOnList: "You're already on the waitlist for this day.",
        failed: "Couldn't join the waitlist. Please try again.",
    },
    errors: {
        chooseLocationFirst: "Please choose a location first.",
        chooseServiceFirst: "Please choose a service first.",
        chooseStaffFirst: "Please choose a staff member first.",
        chooseTimeFirst: "Please choose a time first.",
        fillEverything: "Please fill in everything (service, staff, date, time, name, email).",
        needPhoneForVerification: "Please enter a phone number if you choose phone verification.",
        chooseValidTime: "Please choose a valid time.",
        bookingFailed: "Booking failed",
        loadServicesFailed: "Couldn't load services",
        loadStaffFailed: "Couldn't load staff for this service",
        loadAvailabilityFailed: "Couldn't load availability",
        slotNoLongerAvailable: "This time is no longer available. Please choose another time.",
        invalidEmail: "Please enter a valid email address.",
        bookingsClosed: "Online booking is currently unavailable. Please contact the business directly to make an appointment.",
    },
};

export const STRINGS: Record<Lang, WidgetStrings> = { nl, en };
